/**
 * Season utilities for handling split-year (e.g., "24/25") and calendar-year (e.g., "25") seasons.
 * 
 * Split-year leagues (Premier League, La Liga, etc.): Season spans Aug-Jul across two years
 * Calendar-year leagues (Eliteserien, MLS, etc.): Season spans within a single calendar year
 */

export type SeasonType = 'split-year' | 'calendar-year';

/**
 * Format a Sportmonks season name to short format based on league type.
 * 
 * @example
 * formatSeasonShort("2024/2025", "split-year") // "24/25"
 * formatSeasonShort("2025", "calendar-year") // "25"
 */
export function formatSeasonShort(seasonName: string, seasonType: SeasonType): string {
  if (seasonType === 'calendar-year') {
    // Single year format: "2025" -> "25"
    const match = seasonName.match(/^(\d{4})$/);
    if (match) {
      return match[1].slice(2); // "2025" -> "25"
    }
    // Already short format
    if (/^\d{2}$/.test(seasonName)) {
      return seasonName;
    }
  }
  
  // Split-year format: "2024/2025" -> "24/25"
  const splitMatch = seasonName.match(/^(\d{4})\/(\d{4})$/);
  if (splitMatch) {
    return `${splitMatch[1].slice(2)}/${splitMatch[2].slice(2)}`;
  }
  
  // Already short format like "24/25"
  if (/^\d{2}\/\d{2}$/.test(seasonName)) {
    return seasonName;
  }
  
  // Fallback: return as-is
  return seasonName;
}

/**
 * Parse a short season string to extract years.
 * 
 * @example
 * parseSeasonYears("24/25") // { startYear: 2024, endYear: 2025, type: 'split-year' }
 * parseSeasonYears("25") // { startYear: 2025, endYear: 2025, type: 'calendar-year' }
 */
export function parseSeasonYears(season: string): { 
  startYear: number; 
  endYear: number; 
  type: SeasonType;
} | null {
  // Split-year format: "24/25" or "2024/2025"
  const splitMatch = season.match(/^(\d{2,4})\/(\d{2,4})$/);
  if (splitMatch) {
    let startYear = parseInt(splitMatch[1], 10);
    let endYear = parseInt(splitMatch[2], 10);
    
    // Convert 2-digit to 4-digit years
    if (startYear < 100) {
      startYear += startYear >= 50 ? 1900 : 2000;
    }
    if (endYear < 100) {
      endYear += endYear >= 50 ? 1900 : 2000;
    }
    
    return { startYear, endYear, type: 'split-year' };
  }
  
  // Calendar-year format: "25" or "2025"
  const calMatch = season.match(/^(\d{2,4})$/);
  if (calMatch) {
    let year = parseInt(calMatch[1], 10);
    if (year < 100) {
      year += year >= 50 ? 1900 : 2000;
    }
    return { startYear: year, endYear: year, type: 'calendar-year' };
  }
  
  return null;
}

/**
 * Determine if a season is the current ongoing season based on league type.
 * 
 * For split-year: Current if now is between Aug of startYear and Jul of endYear
 * For calendar-year: Current if the year matches current year
 * 
 * Note: For calendar-year leagues in off-season (Jan-Mar), this returns false.
 * Use getMostRecentSeason() to get the most recent completed season.
 */
export function isCurrentSeason(
  season: string, 
  seasonType: SeasonType,
  referenceDate: Date = new Date()
): boolean {
  const parsed = parseSeasonYears(season);
  if (!parsed) return false;
  
  const now = referenceDate;
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-12
  
  if (seasonType === 'calendar-year' || parsed.type === 'calendar-year') {
    // For calendar-year: season "25" is current during 2025
    // But more specifically, we consider it "current" only during the active months (roughly Mar-Nov)
    // In Jan-Feb, we're in off-season
    return parsed.startYear === currentYear;
  }
  
  // Split-year logic
  const { startYear, endYear } = parsed;
  
  // Season runs from Aug 1 of startYear to Jul 31 of endYear
  if (currentYear === startYear && currentMonth >= 8) {
    return true; // We're in Aug-Dec of the starting year
  }
  if (currentYear === endYear && currentMonth < 8) {
    return true; // We're in Jan-Jul of the ending year
  }
  
  return false;
}

/**
 * Get season boundaries as start and end dates.
 * 
 * Split-year: Aug 1 of startYear to Jul 31 of endYear
 * Calendar-year: Jan 1 to Dec 31 of the year (or Mar 1 to Nov 30 for typical Nordic season)
 */
export function getSeasonBoundaries(
  season: string, 
  seasonType: SeasonType
): { start: Date; end: Date } | null {
  const parsed = parseSeasonYears(season);
  if (!parsed) return null;
  
  if (seasonType === 'calendar-year' || parsed.type === 'calendar-year') {
    // Calendar year: Jan 1 - Dec 31
    return {
      start: new Date(parsed.startYear, 0, 1), // Jan 1
      end: new Date(parsed.endYear, 11, 31),   // Dec 31
    };
  }
  
  // Split-year: Aug 1 - Jul 31
  return {
    start: new Date(parsed.startYear, 7, 1),   // Aug 1
    end: new Date(parsed.endYear, 6, 31),      // Jul 31
  };
}

/**
 * Get the season string for a given date based on league type.
 * 
 * @example
 * extractSeasonFromDate(new Date("2025-10-15"), "split-year") // "25/26"
 * extractSeasonFromDate(new Date("2025-10-15"), "calendar-year") // "25"
 */
export function extractSeasonFromDate(date: Date, seasonType: SeasonType): string {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 1-12
  
  if (seasonType === 'calendar-year') {
    // For calendar-year leagues, the season is simply the year
    return year.toString().slice(2); // "2025" -> "25"
  }
  
  // Split-year logic: Aug 1 is the boundary
  if (month >= 8) {
    // Aug-Dec: we're in the first half of a split season
    const nextYear = year + 1;
    return `${year.toString().slice(2)}/${nextYear.toString().slice(2)}`;
  } else {
    // Jan-Jul: we're in the second half of a split season
    const prevYear = year - 1;
    return `${prevYear.toString().slice(2)}/${year.toString().slice(2)}`;
  }
}

/**
 * Get season boundaries for a league based on type.
 * Returns dates for the current or most recent season.
 * 
 * For calendar-year leagues in off-season, returns the previous (completed) season.
 */
export function getCurrentSeasonDates(
  seasonType: SeasonType,
  referenceDate: Date = new Date()
): { seasonLabel: string; startDate: string; endDate: string; isOffSeason: boolean } {
  const year = referenceDate.getFullYear();
  const month = referenceDate.getMonth() + 1;
  
  if (seasonType === 'calendar-year') {
    // Calendar-year leagues typically run Mar/Apr to Nov
    // In Jan-Feb, we're in off-season - show previous year's completed season
    // In Mar-Nov, we're in the current season
    // In Dec, season just ended - show current year
    
    const isOffSeason = month <= 2; // Jan-Feb is off-season
    const seasonYear = isOffSeason ? year - 1 : year;
    
    return {
      seasonLabel: seasonYear.toString(),
      startDate: `${seasonYear}-01-01`,
      endDate: isOffSeason 
        ? `${seasonYear}-12-31` // Completed season
        : referenceDate.toISOString().slice(0, 10), // Ongoing season
      isOffSeason,
    };
  }
  
  // Split-year leagues
  let seasonStartYear: number;
  if (month >= 8) {
    seasonStartYear = year;
  } else {
    seasonStartYear = year - 1;
  }
  
  const seasonEndYear = seasonStartYear + 1;
  const seasonLabel = `${seasonStartYear.toString().slice(-2)}/${seasonEndYear.toString().slice(-2)}`;
  
  return {
    seasonLabel,
    startDate: `${seasonStartYear}-08-01`,
    endDate: referenceDate.toISOString().slice(0, 10),
    isOffSeason: false, // Split-year leagues are typically always in season
  };
}

/**
 * Format a season for display based on league type.
 * 
 * @example
 * formatSeasonDisplay("25", "calendar-year") // "2025"
 * formatSeasonDisplay("24/25", "split-year") // "2024/25"
 */
export function formatSeasonDisplay(season: string, seasonType: SeasonType): string {
  const parsed = parseSeasonYears(season);
  if (!parsed) return season;
  
  if (seasonType === 'calendar-year' || parsed.type === 'calendar-year') {
    return parsed.startYear.toString();
  }
  
  return `${parsed.startYear}/${parsed.endYear.toString().slice(2)}`;
}

/**
 * Detect season type from a Sportmonks season name.
 * 
 * @example
 * detectSeasonType("2024/2025") // "split-year"
 * detectSeasonType("2025") // "calendar-year"
 */
export function detectSeasonType(seasonName: string): SeasonType {
  // If it contains a slash, it's split-year
  if (seasonName.includes('/')) {
    return 'split-year';
  }
  // Single year format indicates calendar-year
  return 'calendar-year';
}

/**
 * Get the season start year for a given date, based on season type.
 * 
 * Split-year: Aug boundary (month >= 7 → current year, else previous year)
 * Calendar-year: Simply the calendar year
 */
export function getSeasonYearForDate(date: Date, seasonType: SeasonType = 'split-year'): number {
  const month = date.getMonth(); // 0-11
  const year = date.getFullYear();
  if (seasonType === 'calendar-year') {
    return year;
  }
  // Split-year: July (6) is boundary — month >= 6 means current year's season
  return month >= 6 ? year : year - 1;
}

/**
 * Generate the list of seasons that fall within a manager's tenure.
 * 
 * For split-year leagues: generates "YYYY/YY" labels with Aug 1 – Jul 31 boundaries
 * For calendar-year leagues: generates "YY" labels with Jan 1 – Dec 31 boundaries
 */
export function getSeasonsInTenure(
  startDate: string,
  endDate: string | null,
  seasonType: SeasonType = 'split-year'
): { season: string; isPartialFirst: boolean; isPartialLast: boolean }[] {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  const seasons: { season: string; isPartialFirst: boolean; isPartialLast: boolean }[] = [];

  if (seasonType === 'calendar-year') {
    const firstYear = start.getFullYear();
    const lastYear = end.getFullYear();

    for (let year = firstYear; year <= lastYear; year++) {
      const seasonStart = new Date(year, 0, 1); // Jan 1
      const seasonEnd = new Date(year, 11, 31); // Dec 31
      const isPartialFirst = year === firstYear && start > seasonStart;
      const isPartialLast = year === lastYear && endDate != null && end < seasonEnd;
      const label = String(year).slice(-2); // "2026" -> "26"
      seasons.push({ season: label, isPartialFirst, isPartialLast });
    }
    return seasons;
  }

  // Split-year logic
  const firstSeasonYear = getSeasonYearForDate(start, 'split-year');
  const lastSeasonYear = getSeasonYearForDate(end, 'split-year');

  const firstSeasonStart = new Date(firstSeasonYear, 7, 1);
  const isPartialFirst = start > firstSeasonStart;

  const lastSeasonEnd = new Date(lastSeasonYear + 1, 6, 31);
  const isPartialLast = !endDate ? false : end < lastSeasonEnd;

  const firstSeason = `${firstSeasonYear}/${String(firstSeasonYear + 1).slice(-2)}`;

  if (firstSeasonYear === lastSeasonYear) {
    seasons.push({ season: firstSeason, isPartialFirst, isPartialLast });
  } else {
    seasons.push({ season: firstSeason, isPartialFirst, isPartialLast: false });

    for (let year = firstSeasonYear + 1; year < lastSeasonYear; year++) {
      seasons.push({ season: `${year}/${String(year + 1).slice(-2)}`, isPartialFirst: false, isPartialLast: false });
    }

    if (lastSeasonYear > firstSeasonYear) {
      const lastSeason = `${lastSeasonYear}/${String(lastSeasonYear + 1).slice(-2)}`;
      seasons.push({ season: lastSeason, isPartialFirst: false, isPartialLast });
    }
  }

  return seasons;
}

/**
 * Parse a season string to get start/end dates based on season type.
 * 
 * Split-year "2024/25" → Aug 1 2024 – Jul 31 2025
 * Calendar-year "26" → Jan 1 2026 – Dec 31 2026
 */
export function getSeasonDateRange(
  season: string,
  seasonType: SeasonType = 'split-year'
): { start: Date; end: Date } {
  if (seasonType === 'calendar-year') {
    const parsed = parseSeasonYears(season);
    const year = parsed ? parsed.startYear : 2000 + parseInt(season, 10);
    return {
      start: new Date(year, 0, 1),
      end: new Date(year, 11, 31),
    };
  }

  // Split-year
  const parsed = parseSeasonYears(season);
  if (parsed) {
    return {
      start: new Date(parsed.startYear, 7, 1),
      end: new Date(parsed.endYear, 6, 31),
    };
  }

  // Fallback: parse "YYYY/YY" directly
  const startYear = parseInt(season.split('/')[0], 10);
  const fullStartYear = startYear < 100 ? 2000 + startYear : startYear;
  return {
    start: new Date(fullStartYear, 7, 1),
    end: new Date(fullStartYear + 1, 6, 31),
  };
}
